import { useEffect, useState } from 'react';
import { API } from '../App';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Clock, Award, Users, BookOpen, Gift, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';

export default function ActivityFeed() {
  const { t } = useTranslation();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivities();
    // Refresh every 30 seconds
    const interval = setInterval(fetchActivities, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/social/feed?limit=15`, { withCredentials: true });
      setActivities(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching activity feed:', error);
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'transaction_completed':
        return <Clock className="h-5 w-5 text-teal-500" />;
      case 'review_received':
        return <Award className="h-5 w-5 text-yellow-500" />;
      case 'group_joined':
        return <Users className="h-5 w-5 text-purple-500" />;
      case 'achievement_unlocked':
        return <Gift className="h-5 w-5 text-orange-500" />;
      case 'course_created':
        return <BookOpen className="h-5 w-5 text-blue-500" />;
      default:
        return <TrendingUp className="h-5 w-5 text-gray-500" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'transaction_completed':
        return 'border-teal-200 bg-teal-50 dark:bg-teal-900/20 dark:border-teal-800';
      case 'review_received':
        return 'border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800';
      case 'group_joined':
        return 'border-purple-200 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-800';
      case 'achievement_unlocked':
        return 'border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800';
      default:
        return 'border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-700';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Card className="p-6 dark:bg-gray-800">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="h-6 w-6 text-teal-500" />
          <h3 className="text-lg font-bold dark:text-white">Community Activity</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse flex gap-3 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <div className="rounded-full bg-gray-300 dark:bg-gray-600 h-10 w-10"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 dark:bg-gray-800">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <TrendingUp className="h-6 w-6 text-teal-500" />
          <h3 className="text-lg font-bold dark:text-white">Community Activity</h3>
        </div>
        <Badge variant="secondary" className="text-xs">
          Live
        </Badge>
      </div>

      <div className="space-y-3 max-h-[500px] overflow-y-auto">
        {activities.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-400 py-8">
            No recent activity. Be the first! 🚀
          </p>
        ) : (
          activities.map((activity, idx) => (
            <div
              key={activity.id || idx}
              className={`flex gap-3 p-3 rounded-lg border transition-all hover:shadow-md ${getActivityColor(activity.activity_type)}`}
            >
              <Avatar className="h-10 w-10">
                <AvatarImage src={activity.user_picture} />
                <AvatarFallback className="bg-teal-100 text-teal-600 dark:bg-teal-900 dark:text-teal-300">
                  {activity.user_name?.charAt(0) || '?'}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm dark:text-white">
                    <span className="font-semibold">{activity.user_name}</span>
                    {' '}
                    <span className="text-gray-600 dark:text-gray-300">{activity.content}</span>
                  </p>
                  <div className="flex-shrink-0">
                    {getActivityIcon(activity.activity_type)}
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formatTimestamp(activity.created_at)}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {activities.length > 0 && (
        <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-4">
          Updates every 30 seconds
        </p>
      )}
    </Card>
  );
}
